import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import {
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

const WebhookStatsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [eventStats, setEventStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('count');

  // Colori per il grafico a torta
  const COLORS = ['#4caf50', '#f44336', '#ff9800'];

  // Funzione per recuperare i dati delle statistiche
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Chiamata API per recuperare le statistiche
      const response = await api.get('/webhooks/logs/stats');
      
      if (response.data.success) {
        setStats(response.data.stats);
        
        // Prepara i dati per i grafici
        let events = response.data.eventStats || [];
        
        // Filtra gli eventi in base al filtro temporale (simulato)
        if (timeFilter !== 'all') {
          // Nota: questo è solo un filtro simulato, in realtà dovremmo
          // passare il filtro al backend e filtrare lì
          events = events.filter(() => true);
        }
        
        // Ordina gli eventi
        if (sortOrder === 'count') {
          events.sort((a, b) => b.count - a.count);
        } else if (sortOrder === 'name') {
          events.sort((a, b) => a.event.localeCompare(b.event));
        }
        
        // Aggiungi un'etichetta più leggibile
        events = events.map(event => ({
          ...event,
          label: formatEventName(event.event)
        }));
        
        setEventStats(events);
      } else {
        setError(response.data.message || 'Errore nel recupero delle statistiche');
      }
    } catch (err) {
      console.error('Errore nel recupero delle statistiche webhook:', err);
      setError(err.response?.data?.message || err.message || 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [timeFilter, sortOrder]);

  // Recupera i dati all'avvio e quando cambia il filtro
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Formatta il nome dell'evento per renderlo più leggibile
  const formatEventName = (eventName) => {
    if (!eventName) return 'Sconosciuto';
    
    // Dividi per il punto e capitalizza ogni parte
    const parts = eventName.split('.');
    return parts.map(part => 
      part.charAt(0).toUpperCase() + part.slice(1)
    ).join(' - ');
  };

  // Esporta i dati in formato CSV
  const exportCSV = () => {
    if (!stats || !eventStats.length) return;
    
    // Crea l'intestazione
    let csv = 'Categoria,Valore\n';
    csv += `Totale Webhook,${stats.total}\n`;
    csv += `Successi,${stats.success}\n`;
    csv += `Errori,${stats.error}\n`;
    csv += `Tasso di Successo,${stats.successRate}%\n\n`;
    
    // Aggiungi statistiche per evento
    csv += 'Evento,Conteggio\n';
    eventStats.forEach(event => {
      csv += `${event.event},${event.count}\n`;
    });
    
    // Crea un blob e un link per il download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `webhook_stats_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Prepara i dati per il grafico a torta
  const prepareSuccessRateData = () => {
    if (!stats) return [];
    
    return [
      { name: 'Successi', value: stats.success },
      { name: 'Errori', value: stats.error },
    ];
  };

  // Renderizza le metriche principali
  const renderMetrics = () => {
    if (!stats) return null;
    
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Totale Webhook
              </Typography>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
              <Typography color="textSecondary">
                invii totali
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ backgroundColor: '#e8f5e9' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SuccessIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom>
                  Successi
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.success}
              </Typography>
              <Typography color="textSecondary">
                {((stats.success / stats.total) * 100).toFixed(1)}% del totale
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ backgroundColor: '#ffebee' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom>
                  Errori
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.error}
              </Typography>
              <Typography color="textSecondary">
                {((stats.error / stats.total) * 100).toFixed(1)}% del totale
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ScheduleIcon sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom>
                  Tasso di Successo
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.successRate.toFixed(1)}%
              </Typography>
              <Typography color="textSecondary">
                negli ultimi {timeFilter === 'all' ? 'tutti i giorni' : timeFilter}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Renderizza il grafico a torta per successi/errori
  const renderSuccessRateChart = () => {
    const data = prepareSuccessRateData();
    
    return (
      <Paper elevation={2} sx={{ p: 2, height: 300 }}>
        <Typography variant="h6" gutterBottom>
          Distribuzione Successi/Errori
        </Typography>
        <ResponsiveContainer width="100%" height="90%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Legend />
            <RechartsTooltip formatter={(value) => [value, '']} />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  // Renderizza il grafico a barre per gli eventi
  const renderEventsBarChart = () => {
    return (
      <Paper elevation={2} sx={{ p: 2, height: 400 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Eventi Più Frequenti
          </Typography>
          <Tooltip title="Cambia ordinamento">
            <IconButton onClick={() => setSortOrder(sortOrder === 'count' ? 'name' : 'count')}>
              <SortIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer width="100%" height="85%">
          <BarChart
            data={eventStats}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis 
              type="category" 
              dataKey="label" 
              width={100}
              tick={{ fontSize: 12 }}
            />
            <RechartsTooltip
              formatter={(value, name, props) => [value, props.payload.event]}
            />
            <Bar dataKey="count" fill="#2196f3" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Dashboard Statistiche Webhook
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Periodo</InputLabel>
            <Select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              label="Periodo"
            >
              <MenuItem value="all">Tutti i periodi</MenuItem>
              <MenuItem value="7 giorni">Ultimi 7 giorni</MenuItem>
              <MenuItem value="30 giorni">Ultimi 30 giorni</MenuItem>
              <MenuItem value="90 giorni">Ultimi 90 giorni</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchStats}
            disabled={loading}
          >
            Aggiorna
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportCSV}
            disabled={loading || !stats}
          >
            Esporta CSV
          </Button>
        </Box>
      </Box>
      
      {loading && !stats && (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {stats && (
        <>
          {renderMetrics()}
          
          <Box mt={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                {renderSuccessRateChart()}
              </Grid>
              <Grid item xs={12} md={7}>
                {renderEventsBarChart()}
              </Grid>
            </Grid>
          </Box>
          
          <Box mt={4}>
            <Divider />
            <Typography variant="body2" color="textSecondary" mt={1}>
              Nota: I filtri temporali sono simulati nell'interfaccia. Per implementare il filtraggio reale, sarà necessario modificare l'API sul backend.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WebhookStatsDashboard; 